import React, { useEffect } from 'react';
import { ZodType } from 'zod';
import {
  FieldPath,
  FormProvider,
  useForm,
  UseFormProps,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { pickBy } from 'lodash';

export interface FormProps<DATA extends Record<string, any>> {
  onSubmit: (data: DATA) => Promise<any> | any;
  schema: ZodType<DATA>;
  children?: React.ReactNode;
  initialValues?: DATA;
  options?: UseFormProps;
  className?: string;
  useLocation?: boolean;
  resetOnSuccess?: boolean;
  resetDirtyOnSubmit?: boolean;
  serverErrors?: { path: FieldPath<any>; message: string }[];
}

export function Form<T extends Record<string, any>>(props: FormProps<T>) {
  function getValueShape(obj?: Record<string, any>) {
    return pickBy(obj || {}, (v, key) => {
      if ('shape' in props.schema) {
        // eslint-disable-next-line no-prototype-builtins
        return (props.schema as any).shape.hasOwnProperty(key);
      }
      if ('schema' in props.schema._def) {
        // eslint-disable-next-line no-prototype-builtins
        return (props.schema._def as any).schema.shape.hasOwnProperty(key);
      }
    });
  }

  const formProps = useForm<any>({
    defaultValues: {
      ...getValueShape(props.initialValues),
    },
    resolver: props.schema ? zodResolver(props.schema) : undefined,
    ...props.options,
  });

  useEffect(() => {
    const errors = props.serverErrors ?? [];
    for (const error of errors) {
      formProps.setError(error.path, {
        type: 'server',
        message: error.message,
      });
    }
  }, [props.serverErrors]);

  useEffect(() => {
    if (
      (props.resetOnSuccess || props.resetDirtyOnSubmit) &&
      formProps.formState.isSubmitSuccessful
    ) {
      formProps.reset(
        { ...getValueShape(props.initialValues) },
        {
          keepValues: props.resetDirtyOnSubmit,
        },
      );
    }
  }, [formProps.reset, formProps.formState.isSubmitSuccessful]);

  async function onSubmit(data: any) {
    try {
      await props.onSubmit(data);
      // eslint-disable-next-line no-empty
    } catch {}
  }

  return (
    <FormProvider {...formProps}>
      <form
        noValidate
        className={props.className}
        onSubmit={formProps.handleSubmit(onSubmit)}
      >
        {props.children}
      </form>
    </FormProvider>
  );
}
